import React, {useContext} from "react";

import { formatToRandWithDecimalsString } from "../../core/helpers/textFormatting";

import {Incrementer} from "../tools/Incrementer";
import {CloseButton} from "../buttons/CloseButton";
import {HalfwayContext} from "../../context/HalfwayContext";
import {HalfwayContextType} from "../../types/HalfwayContextType";
import {IOptionSet} from "../../core/interfaces/IOptionSet";
import {getMenuItemUnitPrice} from "../../core/helpers/menuItemPricing";
import {
    allowsMultipleSelections,
    clearOptionSetSelections,
    findMissingRequiredOptionSet,
    isOptionSetRequired,
    isOptionActive,
    findUnavailableRequiredOptionSet,
    updateOptionSelections
} from "../../core/helpers/optionSetSelection";

export const ProductDrawer = () => {

    const {
        productDrawerMenuItem,
        setProductDrawerMenuItem,
        toggleProductDrawer,
        addToBasketFunction
    } = useContext(HalfwayContext) as HalfwayContextType;

    let addOrUpdate: string = (productDrawerMenuItem?.totalInBasket ?? 0) > 0
                                ? 'Update Basket'
                                : 'Add to Basket';

    const updateSpecialInstructions = (text: string) =>
    {
        setProductDrawerMenuItem(prevState => ({
            ...prevState,
            specialInstruction: text
        }));
    }

    const updateSelectedOption = (optionSet: IOptionSet, optionId: string) =>
    {
        setProductDrawerMenuItem(prevState => {
            return {
                ...prevState,
                selectedOptions: updateOptionSelections(
                    prevState.selectedOptions ?? [],
                    optionSet,
                    optionId
                )
            };
        });
    }

    const clearSelectedOptions = (optionSet: IOptionSet) =>
    {
        setProductDrawerMenuItem(prevState => ({
            ...prevState,
            selectedOptions: clearOptionSetSelections(
                prevState.selectedOptions ?? [],
                optionSet.id
            )
        }));
    }

    const costOfItemsInBasket = getMenuItemUnitPrice(productDrawerMenuItem) * (productDrawerMenuItem?.totalInBasket ?? 0);
    const unitPrice = getMenuItemUnitPrice(productDrawerMenuItem);
    const unavailableRequiredSet = findUnavailableRequiredOptionSet(productDrawerMenuItem.optionSets ?? []);
    const missingRequiredOptionSet = productDrawerMenuItem.totalInBasket > 0 && findMissingRequiredOptionSet(
        productDrawerMenuItem.optionSets ?? [],
        productDrawerMenuItem.selectedOptions ?? []
    );

    return(
        <div className='flex flex-col h-full min-h-0 overflow-y-auto overscroll-contain bg-white'>

            {/* Drawer Handle */}
            <div className="drawer-handle"></div>

            <div
                className='flex flex-col px-5'
                style={{paddingBottom: 'calc(2rem + env(safe-area-inset-bottom, 0px))'}}>

                {/* Header */}
                <div className='flex justify-between items-start mt-2'>
                    <div className='flex flex-col flex-1 mr-4'>
                        <h2 className='text-xl font-bold text-slate-900 tracking-tight'>
                            {productDrawerMenuItem?.name}
                        </h2>
                        <span className='text-lg font-semibold text-accent-green mt-1'>
                            {formatToRandWithDecimalsString(unitPrice)}
                        </span>
                    </div>
                    <CloseButton closeFunction={() => toggleProductDrawer(productDrawerMenuItem)}/>
                </div>

                {/* Description */}
                {productDrawerMenuItem?.description && (
                    <p className='text-sm text-slate-500 mt-3 leading-relaxed'>
                        {productDrawerMenuItem?.description}
                    </p>
                )}

                {/* Quantity */}
                <div className='mt-5'>
                    <span className='text-xs font-semibold text-slate-500 uppercase tracking-wider'>Quantity</span>
                    <div className='mt-2'>
                        <Incrementer />
                    </div>
                </div>

                {/* Option Sets */}
                {(productDrawerMenuItem.optionSets ?? []).map(optionSet => (
                    <div key={optionSet.id} className='mt-6'>
                        <span className='text-xs font-semibold text-slate-500 uppercase tracking-wider'>
                            {optionSet.name}
                            {isOptionSetRequired(optionSet) && <span className='text-maroon'> *</span>}
                        </span>
                        <span className='text-xs text-slate-400 block mt-1'>
                            {allowsMultipleSelections(optionSet)
                                ? (isOptionSetRequired(optionSet) ? 'Choose at least one' : 'Choose any that apply')
                                : (isOptionSetRequired(optionSet) ? 'Choose one' : 'Choose one or leave blank')
                            }
                        </span>
                        {!(optionSet.options ?? []).some(isOptionActive) && (
                            <p className='text-sm text-red-600 mt-2'>
                                {isOptionSetRequired(optionSet)
                                    ? 'No options are available. This item cannot currently be ordered.'
                                    : 'All options are currently unavailable. You can continue without them.'}
                            </p>
                        )}
                        <div className='flex flex-col gap-2 mt-2'>
                            {!allowsMultipleSelections(optionSet) && !isOptionSetRequired(optionSet) && (
                                <label
                                    className={`flex items-center rounded-xl px-4 py-3 cursor-pointer
                                                transition-all duration-150 ease-out
                                                ${(productDrawerMenuItem.selectedOptions ?? []).some(
                                                    selection => selection.optionSetId === optionSet.id
                                                )
                                                    ? 'bg-slate-50 border-2 border-transparent hover:bg-slate-100'
                                                    : 'bg-maroon-50 border-2 border-maroon shadow-sm'
                                                }`}>
                                    <div className='flex items-center gap-3'>
                                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0
                                                         ${(productDrawerMenuItem.selectedOptions ?? []).some(
                                                             selection => selection.optionSetId === optionSet.id
                                                         )
                                                             ? 'border-slate-300 bg-white'
                                                             : 'border-maroon bg-maroon'
                                                         }`}>
                                            {!(productDrawerMenuItem.selectedOptions ?? []).some(
                                                selection => selection.optionSetId === optionSet.id
                                            ) && <div className='w-2 h-2 rounded-full bg-white'></div>}
                                        </div>
                                        <input
                                            type='radio'
                                            name={`option-set-${productDrawerMenuItem.id}-${optionSet.id}`}
                                            checked={!(productDrawerMenuItem.selectedOptions ?? []).some(
                                                selection => selection.optionSetId === optionSet.id
                                            )}
                                            onChange={() => clearSelectedOptions(optionSet)}
                                            className='sr-only'
                                        />
                                        <span className='text-sm font-medium text-slate-700'>No option</span>
                                    </div>
                                </label>
                            )}
                            {(optionSet.options ?? []).map(option => {
                                const available = isOptionActive(option);
                                const isMultiple = allowsMultipleSelections(optionSet);
                                const additionalPrice = Number(option.additionalPrice ?? 0);
                                const isSelected = (productDrawerMenuItem.selectedOptions ?? []).some(selection =>
                                    selection.optionSetId === optionSet.id &&
                                    (selection.optionId === option.id || selection.optionName === option.name)
                                );

                                return (
                                    <label key={option.id}
                                           className={`flex items-center justify-between rounded-xl px-4 py-3 ${available ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}
                                                       transition-all duration-150 ease-out
                                                       ${isSelected
                                                           ? 'bg-maroon-50 border-2 border-maroon shadow-sm'
                                                           : 'bg-slate-50 border-2 border-transparent hover:bg-slate-100'
                                                       }`}>
                                        <div className='flex items-center gap-3'>
                                            <div className={`w-5 h-5 ${isMultiple ? 'rounded-md' : 'rounded-full'} border-2 flex items-center justify-center flex-shrink-0
                                                             transition-colors duration-150
                                                             ${isSelected
                                                                 ? 'border-maroon bg-maroon'
                                                                 : 'border-slate-300 bg-white'
                                                             }`}>
                                                {isSelected && (
                                                    <div className={`${isMultiple ? 'w-2.5 h-1.5 border-l-2 border-b-2 border-white -rotate-45 -translate-y-px' : 'w-2 h-2 rounded-full bg-white'}`}></div>
                                                )}
                                            </div>
                                            <input
                                                type={isMultiple ? 'checkbox' : 'radio'}
                                                name={`option-set-${productDrawerMenuItem.id}-${optionSet.id}`}
                                                checked={isSelected && available}
                                                disabled={!available}
                                                onChange={() => updateSelectedOption(optionSet, option.id ?? '')}
                                                className='sr-only'
                                            />
                                            <span className={`text-sm font-medium ${isSelected ? 'text-maroon-900' : 'text-slate-700'}`}>
                                                {option.name}
                                            </span>
                                        </div>
                                        <span className={`text-xs font-semibold ${isSelected ? 'text-maroon' : 'text-slate-400'}`}>
                                            {!available ? 'Unavailable' : additionalPrice > 0 ? `+ ${formatToRandWithDecimalsString(additionalPrice)}` : 'Included'}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                ))}

                {/* Special Instructions */}
                <div className='mt-6'>
                    <div className='flex justify-between items-baseline mb-2'>
                        <span className='text-xs font-semibold text-slate-500 uppercase tracking-wider'>
                            Special Instructions
                        </span>
                        <span className='text-xs text-slate-400'>
                            {(productDrawerMenuItem?.specialInstruction ?? '').length}/150
                        </span>
                    </div>
                    <textarea placeholder='Any special requests? (e.g. allergies, extra sauce…)'
                              className='w-full h-28 bg-slate-50 rounded-xl p-4 text-sm text-slate-700 placeholder-slate-400
                                         border-2 border-transparent resize-none
                                         focus:border-maroon focus:outline-none focus:bg-white
                                         transition-all duration-200'
                              maxLength={150}
                              value={productDrawerMenuItem?.specialInstruction ?? ''}
                              onChange={(e) => updateSpecialInstructions(e.target.value)}></textarea>
                </div>

                {/* Add to Basket CTA */}
                <div className='mt-6'>
                    {productDrawerMenuItem.optionAvailabilityChanged && (
                        <p className='text-sm text-amber-800 mb-3' role='status'>
                            An option became unavailable and was removed from your selection. Review your choices before updating the basket.
                        </p>
                    )}
                    {missingRequiredOptionSet && (
                        <p className='text-sm text-red-600 mb-3' role='alert'>
                            {unavailableRequiredSet ? 'This item is currently unavailable. You can set the quantity to zero to remove it.' : `Choose an option for ${missingRequiredOptionSet.name ?? 'the required option set'}.`}
                        </p>
                    )}
                    <button className="w-full disabled:opacity-50 disabled:cursor-not-allowed"
                            type='button'
                            disabled={!!missingRequiredOptionSet}
                            onClick={() => addToBasketFunction(productDrawerMenuItem)}>
                        <div className='button'>
                            <span>{addOrUpdate} — {formatToRandWithDecimalsString(costOfItemsInBasket)}</span>
                        </div>
                    </button>
                </div>

            </div>
        </div>
    )
}
